/**
 * GridView Component
 *
 * Displays documents as cards in a responsive grid layout with drag-and-drop support.
 *
 * @module features/dashboard/components/GridView
 * @see SPEC-016 Requirements 4.1, 4.3, 4.4, 4.6
 */

import React, { useCallback, useState } from 'react';
import { cn } from '../../../lib/cn';
import type { DocListItemInput } from '@linkode/shared';
import { DocCard } from './DocCard';

// ============================================================================
// Types
// ============================================================================

export interface GridViewProps {
  /** Documents to display */
  docs: DocListItemInput[];
  /** Selected document IDs */
  selectedDocIds: string[];
  /** Document click handler */
  onDocClick: (doc: DocListItemInput) => void;
  /** Document context menu handler */
  onDocContextMenu: (doc: DocListItemInput, event: React.MouseEvent) => void;
  /** Document selection toggle handler */
  onDocSelect: (docId: string) => void;
  /** Document move handler (drag-and-drop) */
  onDocMove?: (docId: string, targetFolderPath: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * GridView Component
 *
 * Features:
 * - Responsive grid layout (3-6 cards per row based on viewport)
 * - Document cards with title, preview, and date
 * - Hover overlay with quick actions
 * - Drag-and-drop support for moving documents
 * - Selection support
 *
 * @example
 * ```tsx
 * <GridView
 *   docs={documents}
 *   selectedDocIds={[]}
 *   onDocClick={(doc) => navigate(`/editor/${doc.docId}`)}
 *   onDocContextMenu={(doc, e) => showContextMenu(e, doc)}
 *   onDocSelect={(docId) => toggleSelection(docId)}
 *   onDocMove={(docId, path) => moveDoc(docId, path)}
 * />
 * ```
 *
 * @see Requirements 4.1, 4.3, 4.4, 4.6
 */
export const GridView: React.FC<GridViewProps> = ({
  docs,
  selectedDocIds,
  onDocClick,
  onDocContextMenu,
  onDocSelect,
  onDocMove,
  isLoading = false,
  className,
}) => {
  const [draggedDocId, setDraggedDocId] = useState<string | null>(null);
  const [hoveredDocId, setHoveredDocId] = useState<string | null>(null);

  // Handle drag start
  const handleDragStart = useCallback((event: React.DragEvent, doc: DocListItemInput) => {
    setDraggedDocId(doc.docId);
    event.dataTransfer.setData('text/plain', doc.docId);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedDocId(null);
  }, []);

  // Handle drag over (for folder drop targets)
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop (for folder drop targets)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDrop = useCallback(
    (event: React.DragEvent, targetFolderPath: string) => {
      event.preventDefault();
      const docId = event.dataTransfer.getData('text/plain');
      if (docId && onDocMove) {
        onDocMove(docId, targetFolderPath);
      }
      setDraggedDocId(null);
    },
    [onDocMove]
  );

  return (
    <div
      className={cn(
        'grid gap-4',
        // Responsive grid: 1 col on mobile, 2 on sm, 3 on md, 4 on lg, 5 on xl, 6 on 2xl
        'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6',
        className
      )}
      role="grid"
      aria-label="Document grid"
      aria-busy={isLoading}
      onDragOver={handleDragOver}
    >
      {docs.map((doc) => {
        const isSelected = selectedDocIds.includes(doc.docId);
        const isDragging = draggedDocId === doc.docId;
        const isHovered = hoveredDocId === doc.docId;

        return (
          <div
            key={doc.docId}
            className={cn('relative', isDragging && 'opacity-50')}
            role="gridcell"
            draggable
            onDragStart={(e) => handleDragStart(e, doc)}
            onDragEnd={handleDragEnd}
            onMouseEnter={() => setHoveredDocId(doc.docId)}
            onMouseLeave={() => setHoveredDocId(null)}
          >
            <DocCard
              doc={doc}
              selected={isSelected}
              onClick={onDocClick}
              onContextMenu={onDocContextMenu}
            />

            {/* Hover Overlay with Quick Actions */}
            {isHovered && !isDragging && (
              <div
                className={cn(
                  'absolute top-2 right-2',
                  'flex items-center gap-1',
                  'opacity-0 group-hover:opacity-100',
                  'transition-opacity duration-150'
                )}
              >
                {/* Select Button */}
                <button
                  type="button"
                  className={cn(
                    'p-1.5 rounded-linkode',
                    'bg-white/90 backdrop-blur-sm',
                    'border border-linkode-text/20',
                    'text-linkode-text/70 hover:text-linkode-text',
                    'transition-colors duration-150',
                    'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDocSelect(doc.docId);
                  }}
                  aria-label={isSelected ? 'Deselect document' : 'Select document'}
                >
                  {isSelected ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>

                {/* More Actions Button */}
                <button
                  type="button"
                  className={cn(
                    'p-1.5 rounded-linkode',
                    'bg-white/90 backdrop-blur-sm',
                    'border border-linkode-text/20',
                    'text-linkode-text/70 hover:text-linkode-text',
                    'transition-colors duration-150',
                    'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDocContextMenu(doc, e);
                  }}
                  aria-label="More actions"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Loading Skeleton */}
      {isLoading &&
        Array.from({ length: 6 }).map((_, index) => (
          <div
            key={`skeleton-${index}`}
            className={cn(
              'rounded-linkode border-2 border-linkode-text/20',
              'bg-linkode-bg-light',
              'p-4 h-40',
              'animate-pulse'
            )}
          >
            <div className="h-4 bg-linkode-text/10 rounded w-3/4 mb-3" />
            <div className="h-3 bg-linkode-text/10 rounded w-full mb-2" />
            <div className="h-3 bg-linkode-text/10 rounded w-2/3" />
          </div>
        ))}
    </div>
  );
};

// ============================================================================
// Exports
// ============================================================================

export type { DocListItemInput };
